import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Stack,
    useTheme,
    Box as MuiBox,
    Rating,
    Chip,
    useMediaQuery
} from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import VerifiedIcon from "@mui/icons-material/Verified";

const stories = [
    {
        name: "Rahul Sharma",
        role: "Frontend Developer",
        company: "Tech Mahindra",
        text: "The React & FastAPI course was exactly what I needed to switch from a service-based to a product-based company. The detailed project-based learning is truly world-class.",
        rating: 5,
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rahul",
        gradient: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
    },
    {
        name: "Priya Menon",
        role: "Full Stack Intern",
        company: "Zoho",
        text: "CodingComet's structured learning paths helped me master backend concepts that were previously intimidating. I landed my dream internship within 3 months!",
        rating: 5,
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Priya",
        gradient: "linear-gradient(135deg, #6a0dad 0%, #A380FF 100%)",
    },
    {
        name: "Arjun Verma",
        role: "Backend Engineer",
        company: "Swiggy",
        text: "The Python Advanced modules are incredible. They cover production-grade optimizations that most courses miss. My code efficiency improved by 40%.",
        rating: 5,
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Arjun",
        gradient: "linear-gradient(135deg, #00e676 0%, #00c853 100%)",
    },
    {
        name: "Sneha Kapur",
        role: "DevOps Engineer",
        company: "Amazon (AWS)",
        text: "The Agentic AI modules are ahead of their time. Learning how to integrate AI agents with FastAPI gave me a huge competitive edge in my interviews.",
        rating: 5,
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha",
        gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
    }
];

const SuccessStories = ({ colors, isDark }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const settings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: isMobile ? 1 : isTablet ? 1 : 2,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 6000,
        pauseOnHover: true,
        arrows: false,
        cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
        appendDots: dots => (
            <Box sx={{ mt: 4 }}>
                <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul>
            </Box>
        ),
    };

    return (
        <Box
            sx={{
                py: { xs: 8, md: 4 },
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark ? "#080808" : "#fdfdfd",
            }}
        >
            {/* 🌌 Background Elements */}
            <MuiBox
                sx={{
                    position: "absolute",
                    top: "15%",
                    left: "-5%",
                    width: "350px",
                    height: "350px",
                    background: `radial-gradient(circle, ${colors.primary}12 0%, transparent 70%)`,
                    filter: "blur(70px)",
                    zIndex: 0,
                }}
            />
            <MuiBox
                sx={{
                    position: "absolute",
                    bottom: "15%",
                    right: "-5%",
                    width: "450px",
                    height: "450px",
                    background: `radial-gradient(circle, ${colors.secondary}12 0%, transparent 70%)`,
                    filter: "blur(90px)",
                    zIndex: 0,
                }}
            />

            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h2"
                        fontWeight={900}
                        sx={{
                            mb: 2,
                            fontSize: { xs: "2.2rem", md: "3.2rem" },
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Success Stories
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                            maxWidth: 600,
                            mx: "auto",
                            fontSize: "1.1rem"
                        }}
                    >
                        Empowering the next generation of engineers.
                    </Typography>
                </Box>

                <Box sx={{
                    mx: -2,
                    "& .slick-list": { py: 5 },
                    "& .slick-dots li button:before": {
                        color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                        fontSize: "10px"
                    },
                    "& .slick-dots li.slick-active button:before": { color: colors.primary },
                }}>
                    <Slider {...settings}>
                        {stories.map((story, index) => (
                            <Box key={index} sx={{ px: 2 }}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        borderRadius: "28px",
                                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                                        backdropFilter: "blur(20px)",
                                        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                                        transition: "all 0.4s ease",
                                        position: "relative",
                                        "&:hover": {
                                            transform: "translateY(-8px)",
                                            boxShadow: `0 20px 40px -10px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.08)"}`,
                                            borderColor: colors.primary,
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 4, md: 5 } }}>
                                        <FormatQuoteIcon
                                            sx={{
                                                fontSize: 50,
                                                position: "absolute",
                                                top: 20,
                                                right: 20,
                                                color: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                                            }}
                                        />

                                        <Stack spacing={4}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontSize: "1.1rem",
                                                    fontStyle: "italic",
                                                    lineHeight: 1.7,
                                                    color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
                                                    minHeight: "80px"
                                                }}
                                            >
                                                "{story.text}"
                                            </Typography>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                                                <Box sx={{ position: "relative" }}>
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            inset: -3,
                                                            borderRadius: "50%",
                                                            background: story.gradient,
                                                        }}
                                                    />
                                                    <Avatar
                                                        src={story.avatar}
                                                        sx={{
                                                            width: 64,
                                                            height: 64,
                                                            border: `3px solid ${isDark ? "#080808" : "#fff"}`,
                                                            position: "relative",
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="body1" fontWeight={800}>
                                                            {story.name}
                                                        </Typography>
                                                        <VerifiedIcon sx={{ color: "#3498db", fontSize: 16 }} />
                                                    </Stack>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: colors.primary,
                                                            fontWeight: 700,
                                                            textTransform: "uppercase",
                                                            display: "block",
                                                            mt: 0.5
                                                        }}
                                                    >
                                                        {story.role}
                                                    </Typography>
                                                    <Chip
                                                        label={story.company}
                                                        size="small"
                                                        sx={{
                                                            mt: 1,
                                                            height: 20,
                                                            fontSize: "0.7rem",
                                                            fontWeight: 700,
                                                            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "right" }}>
                                                    <Rating value={story.rating} readOnly size="small" />
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Slider>
                </Box>

                <Box sx={{ textAlign: "center", mt: 12 }}>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={{ xs: 3, md: 6 }}
                        sx={{
                            opacity: 0.4,
                            flexWrap: "wrap",
                            gap: 2
                        }}
                    >
                        {["Google", "Amazon", "Microsoft", "Meta", "Netflix"].map((brand) => (
                            <Typography key={brand} variant="h6" fontWeight={900} sx={{ letterSpacing: "0.15em", fontSize: { xs: "0.8rem", md: "1.1rem" } }}>
                                {brand.toUpperCase()}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default SuccessStories;
